import { Navigate, Route, Routes } from 'react-router-dom'

import Layout from './components/Layout.jsx'
import Commands from './routes/Commands.jsx'
import DocsCards from './routes/DocsCards.jsx'
import DocsEvents from './routes/DocsEvents.jsx'
import DocsHoops from './routes/DocsHoops.jsx'
import DocsIndex from './routes/DocsIndex.jsx'
import DocsMovieNight from './routes/DocsMovieNight.jsx'
import DocsMusic from './routes/DocsMusic.jsx'
import DocsWatch from './routes/DocsWatch.jsx'
import Explainer from './routes/Explainer.jsx'
import Landing from './routes/Landing.jsx'
import NotFound from './routes/NotFound.jsx'
import Releases from './routes/Releases.jsx'
import SignedOut from './routes/SignedOut.jsx'
import Slot from './routes/Slot.jsx'
import TripBoard from './routes/TripBoard.jsx'
import TripCompare from './routes/TripCompare.jsx'
import TripDates from './routes/TripDates.jsx'
import TripPeople from './routes/TripPeople.jsx'
import TripPlace from './routes/TripPlace.jsx'
import Trips from './routes/Trips.jsx'
import HoopsRoutes, { HoopsIndex } from './portal/hoops/HoopsRoutes.jsx'
import WatchArchive from './portal/WatchArchive.jsx'
import WatchGoated from './portal/WatchGoated.jsx'
import WatchLeaderboards from './portal/WatchLeaderboards.jsx'
import WatchMemberProfile from './portal/WatchMemberProfile.jsx'
import WatchTitle from './portal/WatchTitle.jsx'
import { sectionList } from './content.js'

// Section id -> the component that renders it. Anything not listed here gets
// Slot, which routes, renders a heading, and says what it is waiting for.
//
// This is the mechanism spec §5 is asking for: "every later feature ships into
// an existing, consistent surface rather than inventing one under deadline."
// Building the command reference means writing a component and adding one line
// here — the route, the nav entry, the title and the URL already exist.
//
// P6-2 is the first test of that claim, and it held: `commands` and `docs` were
// slots with URLs, nav entries and titles already in sections.json, so shipping
// them was two lines here plus their content. `docs` needed no new page type at
// all — it renders through the same components/DocPage.jsx the explainer does.
//
// 🔴 P6-104 SPLIT `docs` INTO AN INDEX PLUS A PAGE PER FEATURE, and the claim
// above held again: /docs/movie-night and /docs/hoops are four-line components
// over the same components/DocPage.jsx, and the pages with nothing written yet
// were not listed here at all. They fall through to Slot with a real URL and a
// named blocker, which is what a slot is for.
//
// 🔴 P6-165 TOOK TWO OF THEM OFF THE MAP, on Jordan's 2026-09-04 rule that a
// shelved feature's docs come down until that status changes. `docs-pickem` was
// a slot for a cog shelved eighteen days earlier, and `docs-fantasy` was a live
// page until P6-166 shelved its cog. Removing a section from
// content-src/sections.json is what removes the route — this map only supplies
// the component — so an entry left here would be dead rather than dangerous.
// tests/test_shelved_docs.py is what stops either coming back by accident.
//
// 🔴 P6-105 PROMOTED `docs-events` AND LEFT THE OTHER TWO ALONE, which is the
// interesting half. Music and pick'em are not unwritten for want of an author:
// pick'em's cog is shelved so none of its commands are registered, and music is
// a separate deployable whose commands are in no catalog the site can check a
// name against. A page naming a command nobody can type is the exact failure
// src/test/documentation.test.jsx exists to prevent, so both stay slots with
// the reason on the page. content-src/sections.json carries the long version.
//
// 🔴 `architecture` IS GONE FROM THIS MAP, and Explainer is still here. The
// content did not move; its URL did — /architecture is now /docs/how-it-works,
// because Jordan asked for the architecture info to live inside the
// documentation rather than beside it. The old URL is kept alive twice over,
// deliberately: public/_redirects handles a cold request to Cloudflare Pages,
// and REDIRECTS below handles a client-side navigation that never leaves the
// SPA. Neither one covers the other's case.
const PAGES = {
  landing: Landing,
  commands: Commands,
  docs: DocsIndex,
  'docs-movie-night': DocsMovieNight,
  'docs-events': DocsEvents,
  'docs-hoops': DocsHoops,
  // 🔴 P6-206 FILLS THE LAST SLOT, and it is the one that tests the paragraph
  // above hardest. Music was not missing an author — it was missing a way to
  // CHECK a command name, which is a different blocker and the only one here
  // that a component could not have fixed. Two gates now resolve /play, so
  // this is the same one line the other four were.
  'docs-music': DocsMusic,
  // P6-302. The sixth feature page — a four-line component over the same
  // components/DocPage.jsx every sibling here uses. `docs-watch` is a
  // distinct section id from the tier-3 `watch` id further down this map
  // (that one is the title page beneath /watch; this one is /docs/watch),
  // so the two can never collide despite sharing a feature name.
  'docs-watch': DocsWatch,
  // P6-305. The same four-line component over the same components/DocPage.jsx
  // as every sibling here — and the same generated command block as DocsWatch
  // above, scoped to the `cards` feature.
  'docs-cards': DocsCards,
  'docs-how-it-works': Explainer,
  trips: Trips,
  // 🔴 P6-105's claim, tested a third time and held a third time. `releases`
  // has had a URL, a nav entry, a tab and a title in content-src/sections.json
  // since P6-104, and shipping the page was this one line plus a component —
  // no route to invent, no nav to edit, no design to draw.
  releases: Releases,
  // W3b (P6-244). `watch` is a CHILD of `landing` purely so the guard has a
  // literal tier-3 prefix to match `/watch/:subscriptionId` "beneath" — see
  // content-src/sections.json's own comment on the entry. Being a child keeps
  // it off the nav, but this loop still registers `/watch` itself as a real
  // route (it iterates every section, nav or not), so bare `/watch` needs a
  // real answer rather than a stray Slot. `WatchIndex` redirects to `/`,
  // where the panel this title page is one click away from already lives.
  watch: WatchIndex,
  // P6-329c (GN-2c). `hoops` is `watch`'s shape and for the same reason: a child of `landing` that exists so the guard
  // has a literal tier-3 prefix. Bare `/hoops` is a real registered route through this loop, and its answer is the
  // first tab, `/hoops/tonight`; everything beneath it is the one splat route in NESTED below.
  hoops: HoopsIndex,
}

/** `/watch` with no id. Never a page of its own — see the PAGES comment. */
function WatchIndex() {
  return <Navigate to="/" replace />
}

// Routes that are not sections: no nav entry, no tab, no tier badge in the
// registry, because they are not places anybody navigates TO. /signed-out is
// where a sign-in that did not finish lands, and putting it in
// content-src/sections.json would list it in the nav of every page.
const UTILITY = [{ path: '/signed-out', element: <SignedOut /> }]

// URLs that used to be pages. P6-104.
//
// ⚠️ NOT the same job as public/_redirects, and shipping only one of them leaves
// half the traffic broken. That file is read by Cloudflare Pages and answers a
// cold request — somebody opening an old link, or a search result. This answers
// the case Pages never sees: a link inside the app, a back button, or a
// service-worker-served navigation, none of which make a request Pages could
// redirect. src/test/app.test.jsx drives the real <App/> at this path.
//
// `replace` so the dead URL does not sit in history behind the live one — a back
// button that lands on a redirect and bounces forward again is a trap.
const REDIRECTS = [{ from: '/architecture', to: '/docs/how-it-works' }]

// Routes that live BENEATH a section rather than beside it.
//
// 🔴 /trips/:tripId is tier 3 and it is guarded, but not by a second entry
// anywhere — functions/_lib/guard.js matches "exact match, or anything beneath
// it: /trips guards /trips/42 too, which is what T4 will need and what a guard
// keyed on exact paths would silently miss." That sentence was written by T3
// for this route, and this is the route it was written for.
//
// ⚠️ CLAUDE.md gotcha 21 is the reason it is stated here at all: a guard and a
// router can disagree about what a path is, and the way that gets caught is by
// driving the real consumer rather than a model of it.
// src/test/app.test.jsx renders <App/> at every spelling of this path that the
// router accepts and asserts isGuardedPath() agrees about each one.
//
// 🔴 P5-3 T6 ADDS A SECOND ONE AND IT IS GUARDED BY THE SAME SENTENCE. The
// guard matches "exact match, or anything beneath it", so /trips/42/dates is
// covered by the /trips entry with no second registry line — and
// src/test/app.test.jsx drives the REAL <App/> at every spelling of it that
// react-router accepts and asserts isGuardedPath() agrees about each one,
// rather than reading either matcher and inferring.
//
// 🔴 P5-3 T9 ADDS TWO MORE, GUARDED BY THE SAME SENTENCE AND CHECKED THE SAME
// WAY. `/trips/:tripId/compare` and `/trips/:tripId/places/:placeId` are both
// beneath /trips, so `isGuardedPath` covers them with no registry line — and
// src/test/app.test.jsx drives the REAL <App/> at every spelling of each that
// react-router accepts and asserts the guard agrees about the ones it served,
// rather than reading either matcher and inferring. Gotcha 21's finding was
// that ten mutations of the guard missed the bypass because the defect lived in
// the SEAM; the only instrument that has ever caught it here is the router
// itself.
//
// ⚠️ The compare route EXISTING is not a violation of HANDOFF's hardest rule
// and its RENDERING would be. At fewer than two places the component returns
// <Navigate> before it renders anything at all — see TripCompare.jsx, and
// src/test/trips/compare-disclosure.test.jsx, which asserts it against the
// document rather than against a component name.
//
// 🔴 P6-81 ADDS A FIFTH, AND IT IS THE ONE WHERE THE SEAM WOULD HAVE COST
// SOMETHING. `/trips/:tripId/people` is beneath /trips so `isGuardedPath`
// covers it with no registry line — and unlike the four above, what is behind
// it is a list of everybody in the server by name. Gotcha 21's first instance
// closes with *"nothing leaked — nothing sensitive was behind that route yet"*;
// this is the route that makes that sentence stop being true, so the same
// spellings are driven through the REAL <App/> and the guard is asked about
// each one, rather than either matcher being read and the agreement inferred.
//
// ⚠️ Its own route rather than a panel on the board, and the reason is
// structural rather than aesthetic: TripBoard REDIRECTS to the comparison when
// a trip has two or more places and no winner (HANDOFF's rule 3). A panel
// there would be unreachable on exactly the trips most likely to have people
// to add.
// 🔴 W3b (P6-244) ADDS A SIXTH, AND IT IS THE FIRST ONE NOT BENEATH `trips`.
// `/watch/:subscriptionId` is guarded by the SAME mechanism as every route
// above it — functions/_lib/guard.js matches "exact match, or anything
// beneath it", and content-src/sections.json's `watch` entry (tier 3, path
// `/watch`) is what makes that true for this one. `id: 'watch'` looks up that
// section below, the same join every `trips` row already does.
//
// ⚠️ CLAUDE.md gotcha 21 is why it is stated here at all, again: a guard and a
// router can disagree about what a path is, and the way that gets caught is
// by driving the real consumer rather than a model of it.
// src/test/app.test.jsx renders <App/> at every spelling of this path react-
// router accepts and asserts isGuardedPath() agrees about each one.
//
// 🔴 V2-2b ADDS A SEVENTH, `/watch/archive`, ONE MORE STATIC SIBLING OF
// `/watch/:subscriptionId` BENEATH THE SAME GUARDED PREFIX. `isGuardedPath`
// matches "exact match, or anything beneath it" against the literal `/watch`
// section entry, so a new literal route under it needs no new section and no
// guard change — the same free ride `/trips/:tripId/people` got one row up.
// react-router itself resolves the ambiguity with `/watch/:subscriptionId`
// (a static segment always outranks a dynamic one, regardless of array
// order), which src/test/app.test.jsx's own "the archive route" block
// drives directly rather than assuming.
//
// ⚠️ THE PATH SAYS "archive," NOT "everything" — a correction from Jordan,
// mid-slice: the design's own prototype never shows "Everything" as visible
// text (its own heading is "Archive"), and a URL is member-visible too. See
// `WatchArchive.jsx`'s own header for the full account.
//
// 🔴 V2-2c ADDS AN EIGHTH, `/watch/member/:memberId` — TWO SEGMENTS BENEATH
// `/watch`, so it cannot collide with either sibling above: `isGuardedPath`
// still matches it ("beneath /watch"), and react-router never has to choose
// between it and `/watch/:subscriptionId` (one segment) or `/watch/archive`
// (a different literal). `id: 'watch'` looks up the SAME section entry
// every route in this array already does. `src/test/app.test.jsx` drives
// the real router at every spelling it accepts, the same discipline gotcha
// 21 asks every route on this list to be checked with.
//
// 🔴 V2-5c ADDS A NINTH, `/watch/leaderboards` — ONE MORE STATIC SIBLING OF
// `/watch/archive` BENEATH THE SAME GUARDED PREFIX. Exactly the archive
// route's own reasoning, restated because it is the reasoning: a new
// literal route under an already-guarded prefix needs no new section and
// no guard change, and react-router resolves the static/dynamic ambiguity
// with `/watch/:subscriptionId` on its own (a static segment always
// outranks a dynamic one). `src/test/app.test.jsx`'s "the leaderboards
// route" block drives it directly rather than assuming.
//
// 🔴 V2-8c ADDS A TENTH, `/watch/goated` — ONE MORE STATIC SIBLING OF
// `/watch/archive` AND `/watch/leaderboards` BENEATH THE SAME GUARDED
// PREFIX. The identical reasoning again: no new section, no guard change,
// and react-router's own static-over-dynamic rule keeps it distinct from
// `/watch/:subscriptionId`. `src/test/app.test.jsx`'s "the goated route"
// block drives it directly.
//
// P6-329c (GN-2c) ADDS AN ELEVENTH, AND IT IS THE FIRST THAT IS A SUBTREE AND NOT A
// PATH: `/hoops/*`, whose element is `portal/hoops/HoopsRoutes.jsx`, a descendant
// `<Routes>` that answers Tonight, Players, a player, Leaders, Standings, Teams and a
// team. It is one entry rather than seven for a reason that is not tidiness: the season
// views share what they read (the list of seasons, the teams table, every file already
// fetched) through a PARENT route, and react-router keeps a parent mounted while its
// children change; a flat table of seven siblings would remount that parent, and read it
// all again, on every tap. The guard needs nothing new: `/hoops` is a tier-3 section
// (content-src/sections.json, a child of `landing` like `watch`), and "exact match, or
// anything beneath it" is what guards every path below it. `src/test/portal/
// hoops-routes.test.jsx` drives the REAL `<App/>` at every spelling the router accepts
// for each of them and asks `isGuardedPath` about each one, because gotcha 21's finding
// is that the only instrument that has ever caught a guard and a router disagreeing
// about a path is the router.
const NESTED = [
  { id: 'trips', path: '/trips/:tripId', element: TripBoard },
  { id: 'trips', path: '/trips/:tripId/dates', element: TripDates },
  { id: 'trips', path: '/trips/:tripId/compare', element: TripCompare },
  { id: 'trips', path: '/trips/:tripId/people', element: TripPeople },
  { id: 'trips', path: '/trips/:tripId/places/:placeId', element: TripPlace },
  { id: 'watch', path: '/watch/archive', element: WatchArchive },
  { id: 'watch', path: '/watch/leaderboards', element: WatchLeaderboards },
  { id: 'watch', path: '/watch/goated', element: WatchGoated },
  { id: 'watch', path: '/watch/member/:memberId', element: WatchMemberProfile },
  { id: 'watch', path: '/watch/:subscriptionId', element: WatchTitle },
  { id: 'hoops', path: '/hoops/*', element: HoopsRoutes },
]

export default function App() {
  return (
    <Layout>
      <Routes>
        {sectionList.map((section) => {
          const Page = PAGES[section.id] ?? Slot
          return <Route key={section.id} path={section.path} element={<Page section={section} />} />
        })}
        {NESTED.map((route) => {
          const Page = route.element
          const section = sectionList.find((entry) => entry.id === route.id)
          return <Route key={route.path} path={route.path} element={<Page section={section} />} />
        })}
        {UTILITY.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
        {REDIRECTS.map((route) => (
          <Route
            key={route.from}
            path={route.from}
            element={<Navigate to={route.to} replace />}
          />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  )
}
